import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Search,
  Download,
  FileSpreadsheet,
  Printer,
  ArrowUpDown,
  RefreshCw,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  SlidersHorizontal,
  X,
  RotateCcw,
  Receipt,
  Layers,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingState } from '@/components/common/LoadingState';
import { PageHeader } from '@/components/common/PageHeader';
import { PesoIcon } from '@/components/common/PesoIcon';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import { YearSelector } from '@/components/reports/YearSelector';
import { KpiCardGroup } from '@/components/common/KpiCardGroup';
import { KpiCard } from '@/components/common/KpiCard';
import { ledgerService, GeneralLedgerEntry, LedgerSummaryStats } from '@/services/ledgerService';
import { formatCurrency, formatDate, formatNumber } from '@/lib/utils';
import * as XLSX from 'xlsx';

type SortField =
  | 'rowIndex'
  | 'date'
  | 'raiser'
  | 'batch'
  | 'supplier'
  | 'customer'
  | 'ledgerNr'
  | 'ledgerDescription'
  | 'invoiceRef'
  | 'qty'
  | 'qtyKg'
  | 'price'
  | 'total';

export const LedgerPage: React.FC = () => {
  const [entries, setEntries] = useState<GeneralLedgerEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Dynamic Reporting Year state (defaults to latest year containing data, e.g. '2026')
  const [selectedYear, setSelectedYear] = useState<string>('2026');

  // Primary Filters
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedBatch, setSelectedBatch] = useState<string>('all');
  const [selectedAccount, setSelectedAccount] = useState<string>('all');

  // Secondary Filters ("More Filters" panel)
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(false);
  const [selectedRaiser, setSelectedRaiser] = useState<string>('all');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Sorting: Default to Date descending for operational efficiency
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortAsc, setSortAsc] = useState<boolean>(false);

  // Progressive disclosure: Track expanded rows by ID
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());

  const toggleRowExpansion = (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ledgerService.getLedgerEntries();
      setEntries(data);
    } catch (err: any) {
      console.error('[LedgerPage] Error loading ledger entries:', err);
      setError(err.message || 'Failed to load General Ledger entries.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // 1. Derive available reporting years dynamically from live transaction dates
  const availableYears = useMemo(() => {
    const yearsSet = new Set<string>();
    for (const e of entries) {
      if (e.date && e.date.length >= 4) {
        const y = e.date.substring(0, 4);
        if (/^\d{4}$/.test(y)) yearsSet.add(y);
      }
    }
    const sorted = Array.from(yearsSet).sort().reverse();
    return sorted.length > 0 ? sorted : ['2026', '2025', '2024'];
  }, [entries]);

  // Set default year if current selection is invalid
  useEffect(() => {
    if (availableYears.length > 0 && selectedYear !== 'all' && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // 2. Base entries filtered by selected Reporting Year (using transaction posting date)
  const yearEntries = useMemo(() => {
    if (selectedYear === 'all') return entries;
    return entries.filter((e) => e.date && e.date.startsWith(selectedYear));
  }, [entries, selectedYear]);

  // 3. Derived filter options based on yearEntries
  const batchOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of yearEntries) {
      if (e.batch && e.batch !== '—') set.add(e.batch);
    }
    return Array.from(set).sort();
  }, [yearEntries]);

  const accountOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of yearEntries) {
      if (e.ledgerNr) {
        map.set(e.ledgerNr, `${e.ledgerNr} — ${e.ledgerDescription || 'Account'}`);
      }
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [yearEntries]);

  const raiserOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of yearEntries) {
      if (e.raiser && e.raiser !== '—') set.add(e.raiser);
    }
    return Array.from(set).sort();
  }, [yearEntries]);

  const supplierOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of yearEntries) {
      if (e.supplier && e.supplier.trim()) set.add(e.supplier);
    }
    return Array.from(set).sort();
  }, [yearEntries]);

  const customerOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of yearEntries) {
      if (e.customer && e.customer.trim()) set.add(e.customer);
    }
    return Array.from(set).sort();
  }, [yearEntries]);

  // Active secondary filters count
  const activeSecondaryCount = [
    selectedRaiser !== 'all',
    selectedSupplier !== 'all',
    selectedCustomer !== 'all',
    Boolean(startDate),
    Boolean(endDate),
  ].filter(Boolean).length;

  // 4. Fully filtered and sorted dataset
  const filteredEntries = useMemo(() => {
    let result = yearEntries;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (e) =>
          (e.date && e.date.includes(q)) ||
          (e.raiser && e.raiser.toLowerCase().includes(q)) ||
          (e.batch && e.batch.toLowerCase().includes(q)) ||
          (e.supplier && e.supplier.toLowerCase().includes(q)) ||
          (e.customer && e.customer.toLowerCase().includes(q)) ||
          (e.ledgerNr && e.ledgerNr.includes(q)) ||
          (e.ledgerDescription && e.ledgerDescription.toLowerCase().includes(q)) ||
          (e.invoiceRef && e.invoiceRef.toLowerCase().includes(q)) ||
          (e.description && e.description.toLowerCase().includes(q))
      );
    }

    if (selectedBatch !== 'all') {
      result = result.filter((e) => e.batch === selectedBatch);
    }

    if (selectedAccount !== 'all') {
      result = result.filter((e) => e.ledgerNr === selectedAccount);
    }

    if (selectedRaiser !== 'all') {
      result = result.filter((e) => e.raiser === selectedRaiser);
    }

    if (selectedSupplier !== 'all') {
      result = result.filter((e) => e.supplier === selectedSupplier);
    }

    if (selectedCustomer !== 'all') {
      result = result.filter((e) => e.customer === selectedCustomer);
    }

    if (startDate) {
      result = result.filter((e) => e.date && e.date >= startDate);
    }

    if (endDate) {
      result = result.filter((e) => e.date && e.date <= endDate);
    }

    return result.sort((a, b) => {
      let valA: any = a[sortField];
      let valB: any = b[sortField];

      if (valA === null || valA === undefined) valA = sortAsc ? Infinity : -Infinity;
      if (valB === null || valB === undefined) valB = sortAsc ? Infinity : -Infinity;

      if (typeof valA === 'string') {
        return sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      return sortAsc ? valA - valB : valB - valA;
    });
  }, [
    yearEntries,
    searchQuery,
    selectedBatch,
    selectedAccount,
    selectedRaiser,
    selectedSupplier,
    selectedCustomer,
    startDate,
    endDate,
    sortField,
    sortAsc,
  ]);

  // 5. Dynamic Summary Statistics for Top 4 KPI Cards
  const summaryStats: LedgerSummaryStats = useMemo(() => {
    return ledgerService.computeSummaryStats(filteredEntries);
  }, [filteredEntries]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(field === 'rowIndex');
    }
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedBatch('all');
    setSelectedAccount('all');
    setSelectedRaiser('all');
    setSelectedSupplier('all');
    setSelectedCustomer('all');
    setStartDate('');
    setEndDate('');
    setSortField('date');
    setSortAsc(false);
  };

  // Export Handlers preserving full 13 authoritative columns
  const handleExportCSV = () => {
    if (filteredEntries.length === 0) return;

    const headers = [
      'Date',
      'Raiser',
      'Batch',
      'Supplier',
      'Customer',
      'Ledger Nr',
      'Ledger Description',
      'Invoice Ref #',
      'Description',
      'Qty',
      'Qty Kg',
      'Price',
      'Total',
    ];

    const rows = filteredEntries.map((e) => [
      e.date,
      e.raiser,
      e.batch,
      e.supplier || '',
      e.customer || '',
      e.ledgerNr,
      e.ledgerDescription,
      e.invoiceRef || '',
      e.description || '',
      e.qty ?? '',
      e.qtyKg ?? '',
      e.price ?? '',
      e.total,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `VSB_General_Ledger_${selectedYear}_${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportExcel = () => {
    if (filteredEntries.length === 0) return;

    const exportRows = filteredEntries.map((e) => ({
      Date: e.date,
      Raiser: e.raiser,
      Batch: e.batch,
      Supplier: e.supplier || '',
      Customer: e.customer || '',
      'Ledger Nr': e.ledgerNr,
      'Ledger Description': e.ledgerDescription,
      'Invoice Ref #': e.invoiceRef || '',
      Description: e.description || '',
      Qty: e.qty,
      'Qty Kg': e.qtyKg,
      Price: e.price,
      Total: e.total,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `Ledger_${selectedYear}`);

    XLSX.writeFile(
      workbook,
      `VSB_General_Ledger_${selectedYear}_${new Date().toISOString().split('T')[0]}.xlsx`
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const isAllYears = selectedYear === 'all';
  const yearTitle = isAllYears ? 'All Years' : selectedYear;

  return (
    <div className="space-y-5 max-w-7xl mx-auto pb-12">
      {/* 1. Standardized PageHeader with Dynamic Year Selector */}
      <PageHeader
        title={`General Ledger — ${yearTitle}`}
        subtitle="Authoritative transactional ledger matching master 13-column Excel structure. Filtered on transaction posting date."
        icon={BookOpen}
        helpScreenKey="ledger"
        actions={
          <>
            <ActionTooltip content="Reload complete 13-column financial transaction entries.">
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
                disabled={loading}
                className="text-xs h-8 sm:h-9 font-semibold border-slate-300 hover:bg-slate-50 gap-1.5"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </ActionTooltip>

            <ActionTooltip content="Download filtered transactions to a CSV spreadsheet.">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={filteredEntries.length === 0}
                className="text-xs h-8 sm:h-9 font-semibold border-slate-300 hover:bg-slate-50 gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />
                <span>CSV</span>
              </Button>
            </ActionTooltip>

            <ActionTooltip content="Download filtered transactions to formatted Excel (.xlsx) workbook.">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                disabled={filteredEntries.length === 0}
                className="text-xs h-8 sm:h-9 font-semibold border-emerald-300 text-emerald-800 bg-emerald-50/50 hover:bg-emerald-100 gap-1.5"
              >
                <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-700" />
                <span>Excel</span>
              </Button>
            </ActionTooltip>

            <ActionTooltip content="Open print-optimized view for accounting ledger records.">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="text-xs h-8 sm:h-9 font-semibold border-slate-300 hover:bg-slate-50 gap-1.5 hidden md:inline-flex"
              >
                <Printer className="w-3.5 h-3.5 text-slate-600" />
                <span>Print</span>
              </Button>
            </ActionTooltip>
          </>
        }
        yearSelector={
          <YearSelector
            availableYears={availableYears}
            selectedYear={selectedYear}
            onYearChange={(y) => {
              setSelectedYear(y);
              setSelectedBatch('all');
            }}
          />
        }
      />

      {/* Print View Header */}
      <div className="hidden print:block mb-4 border-b pb-2">
        <h1 className="text-xl font-bold text-slate-900">VSB Farm Management — General Ledger ({yearTitle})</h1>
        <p className="text-xs text-slate-500">
          Generated on {new Date().toLocaleDateString('en-PH', { dateStyle: 'full' })} • Filtered Rows: {filteredEntries.length.toLocaleString()} • Total Booked Volume: {formatCurrency(summaryStats.totalAmount)}
        </p>
      </div>

      {/* 2. Standardized 4 KPI Summary Cards (Dynamic to Selected Year) */}
      <KpiCardGroup columns={4}>
        <KpiCard
          title="Transactions"
          value={summaryStats.totalRows.toLocaleString()}
          subtitle={isAllYears ? 'All 2,705 authoritative rows' : `${summaryStats.totalRows.toLocaleString()} in ${selectedYear}`}
          icon={Receipt}
          variant="default"
        />
        <KpiCard
          title="Total Booked Volume"
          value={formatCurrency(summaryStats.totalAmount)}
          subtitle="Total debits & credits booked"
          icon={PesoIcon}
          variant="default"
        />
        <KpiCard
          title="Pig Sales (1010)"
          value={formatCurrency(summaryStats.salesRevenue)}
          subtitle="Gross commercial pig sales"
          icon={PesoIcon}
          variant="emerald"
        />
        <KpiCard
          title="Production Costs (3000s)"
          value={formatCurrency(summaryStats.productionCost)}
          subtitle="Feeds, piglets, meds & expenses"
          icon={Receipt}
          variant="default"
        />
      </KpiCardGroup>

      {/* 3. Filter Toolbar: Primary Controls + Expandable Secondary Filters */}
      <Card className="border-slate-200 shadow-xs print:hidden">
        <CardContent className="p-3.5 space-y-3">
          {/* Primary Filter Row */}
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2.5">
            {/* Search Input */}
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="text"
                placeholder="Search raiser, batch, supplier, customer, invoice #, account, description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-xs border-slate-300"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Batch Filter */}
            <div className="min-w-[130px]">
              <select
                value={selectedBatch}
                onChange={(e) => setSelectedBatch(e.target.value)}
                aria-label="Filter by Batch"
                className="w-full h-9 px-2.5 text-xs bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-900 text-slate-700 font-medium"
              >
                <option value="all">All Batches</option>
                {batchOptions.map((b) => (
                  <option key={b} value={b}>
                    Batch {b}
                  </option>
                ))}
              </select>
            </div>

            {/* Account Code Filter */}
            <div className="min-w-[170px]">
              <select
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                aria-label="Filter by Account Code"
                className="w-full h-9 px-2.5 text-xs bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-900 text-slate-700 font-medium"
              >
                <option value="all">All Accounts (1010–4030)</option>
                {accountOptions.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* More Filters Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMoreFilters(!showMoreFilters)}
              className={`h-9 px-3 text-xs font-semibold border-slate-300 transition-colors flex items-center gap-1.5 ${
                showMoreFilters || activeSecondaryCount > 0
                  ? 'bg-slate-100 text-slate-900 border-slate-400'
                  : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span>More Filters</span>
              {activeSecondaryCount > 0 && (
                <span className="ml-1 px-1.5 py-0.2 bg-emerald-700 text-white rounded-full text-[10px] font-bold">
                  {activeSecondaryCount}
                </span>
              )}
            </Button>

            {/* Reset Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetFilters}
              className="h-9 px-2.5 text-xs text-slate-500 hover:text-slate-800 hover:bg-slate-100"
              title="Reset all filters"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" />
              Reset
            </Button>
          </div>

          {/* Expandable Secondary Filter Panel ("More Filters") */}
          {showMoreFilters && (
            <div className="pt-3 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 bg-slate-50/70 p-3 rounded-xl">
              {/* Raiser Filter */}
              <div>
                <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Raiser</label>
                <select
                  value={selectedRaiser}
                  onChange={(e) => setSelectedRaiser(e.target.value)}
                  className="w-full h-8 px-2 text-xs bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-900"
                >
                  <option value="all">All Raisers ({raiserOptions.length})</option>
                  {raiserOptions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              {/* Supplier Filter */}
              <div>
                <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Supplier</label>
                <select
                  value={selectedSupplier}
                  onChange={(e) => setSelectedSupplier(e.target.value)}
                  className="w-full h-8 px-2 text-xs bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-900"
                >
                  <option value="all">All Suppliers ({supplierOptions.length})</option>
                  {supplierOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              {/* Customer Filter */}
              <div>
                <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Customer / Buyer</label>
                <select
                  value={selectedCustomer}
                  onChange={(e) => setSelectedCustomer(e.target.value)}
                  className="w-full h-8 px-2 text-xs bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-900"
                >
                  <option value="all">All Customers ({customerOptions.length})</option>
                  {customerOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range Filters */}
              <div>
                <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Posting Date Range</label>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="text-xs h-8 p-1 border-slate-300 bg-white"
                  />
                  <span className="text-slate-400 text-xs">to</span>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="text-xs h-8 p-1 border-slate-300 bg-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Active Filter Count Footer */}
          <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
            <span>
              Showing <span className="font-bold text-slate-900">{filteredEntries.length.toLocaleString()}</span> of{' '}
              <span className="font-bold text-slate-900">{yearEntries.length.toLocaleString()}</span> records in {yearTitle}
            </span>
            {filteredEntries.length < yearEntries.length && (
              <span className="text-amber-800 font-medium">Filtered active view</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 4. Desktop 8-Column Table with Progressive Disclosure (Zero Horizontal Scroll on 1280px) */}
      {loading ? (
        <LoadingState message="Loading Authoritative General Ledger records..." />
      ) : filteredEntries.length === 0 ? (
        <Card className="border-slate-200 shadow-sm p-12 text-center">
          <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-sm font-bold text-slate-800">No Ledger Entries Found</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
            No transactions match the selected filters or search parameters for {yearTitle}. Try resetting your search filters.
          </p>
          <Button variant="outline" size="sm" onClick={handleResetFilters} className="mt-4 text-xs">
            Reset Filters
          </Button>
        </Card>
      ) : (
        <>
          {/* Desktop Table View (Hidden on Mobile) */}
          <div className="hidden md:block bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-left text-xs border-collapse font-sans">
              <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200 select-none">
                <tr>
                  {/* 1. Date */}
                  <th
                    onClick={() => handleSort('date')}
                    className="py-2.5 px-3 cursor-pointer hover:bg-slate-200 w-28 whitespace-nowrap"
                  >
                    <div className="flex items-center gap-1">
                      <span>Date</span>
                      <ArrowUpDown className="w-3 h-3 text-slate-400" />
                    </div>
                  </th>

                  {/* 2. Raiser */}
                  <th
                    onClick={() => handleSort('raiser')}
                    className="py-2.5 px-3 cursor-pointer hover:bg-slate-200 w-36 whitespace-nowrap"
                  >
                    <div className="flex items-center gap-1">
                      <span>Raiser</span>
                      <ArrowUpDown className="w-3 h-3 text-slate-400" />
                    </div>
                  </th>

                  {/* 3. Batch */}
                  <th
                    onClick={() => handleSort('batch')}
                    className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-200 w-20 text-center whitespace-nowrap"
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span>Batch</span>
                      <ArrowUpDown className="w-3 h-3 text-slate-400" />
                    </div>
                  </th>

                  {/* 4. Ledger Nr */}
                  <th
                    onClick={() => handleSort('ledgerNr')}
                    className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-200 text-center w-20 whitespace-nowrap"
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span>Account</span>
                      <ArrowUpDown className="w-3 h-3 text-slate-400" />
                    </div>
                  </th>

                  {/* 5. Ledger Description */}
                  <th className="py-2.5 px-3 w-40 whitespace-nowrap">Account Name</th>

                  {/* 6. Description */}
                  <th className="py-2.5 px-3 min-w-[180px]">Description</th>

                  {/* 7. Qty / Qty Kg */}
                  <th className="py-2.5 px-3 text-right w-28 whitespace-nowrap">Qty / Weight</th>

                  {/* 8. Total */}
                  <th
                    onClick={() => handleSort('total')}
                    className="py-2.5 px-3 text-right cursor-pointer hover:bg-slate-200 font-bold text-slate-900 w-32 whitespace-nowrap"
                  >
                    <div className="flex items-center justify-end gap-1">
                      <span>Total (₱)</span>
                      <ArrowUpDown className="w-3 h-3 text-slate-400" />
                    </div>
                  </th>

                  {/* 9. Action (Expand Toggle) */}
                  <th className="py-2.5 px-2 text-center w-10 print:hidden"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {filteredEntries.map((e) => {
                  const isExpanded = expandedRowIds.has(e.id);
                  const isRevenue = e.ledgerNr === '1010' || e.ledgerNr === '1011';

                  let qtyStr = '—';
                  if (e.qty && e.qtyKg) {
                    qtyStr = `${e.qty} hd / ${formatNumber(e.qtyKg)} kg`;
                  } else if (e.qty) {
                    qtyStr = e.ledgerNr === '3020' ? `${e.qty} bags` : `${e.qty} units`;
                  } else if (e.qtyKg) {
                    qtyStr = `${formatNumber(e.qtyKg)} kg`;
                  }

                  return (
                    <React.Fragment key={e.id}>
                      <tr
                        onClick={() => toggleRowExpansion(e.id)}
                        className={`hover:bg-slate-50/90 cursor-pointer transition-colors ${
                          isExpanded ? 'bg-slate-50 font-medium' : ''
                        }`}
                      >
                        {/* 1. Date */}
                        <td className="py-2.5 px-3 whitespace-nowrap font-mono text-slate-700 font-medium text-[11px]">
                          {e.date ? formatDate(e.date) : '—'}
                        </td>

                        {/* 2. Raiser */}
                        <td className="py-2.5 px-3 font-semibold text-slate-900 truncate max-w-[140px]">
                          {e.cycleId ? (
                            <Link
                              to={`/cycles/${e.cycleId}`}
                              onClick={(ev) => ev.stopPropagation()}
                              className="text-indigo-700 hover:text-indigo-950 hover:underline"
                            >
                              {e.raiser}
                            </Link>
                          ) : (
                            e.raiser
                          )}
                        </td>

                        {/* 3. Batch */}
                        <td className="py-2.5 px-2.5 text-center font-mono text-slate-700 text-[11px]">
                          {e.batch}
                        </td>

                        {/* 4. Ledger Nr */}
                        <td className="py-2.5 px-2.5 text-center">
                          <span
                            className={`px-1.5 py-0.5 rounded font-mono font-bold text-[10px] border ${
                              isRevenue
                                ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                                : 'bg-slate-100 text-slate-800 border-slate-300'
                            }`}
                          >
                            {e.ledgerNr}
                          </span>
                        </td>

                        {/* 5. Ledger Description */}
                        <td className="py-2.5 px-3 text-slate-700 truncate max-w-[160px] text-[11px]">
                          {e.ledgerDescription}
                        </td>

                        {/* 6. Description */}
                        <td className="py-2.5 px-3 text-slate-600 truncate max-w-[240px] text-[11px]">
                          {e.description || <span className="text-slate-300 font-mono">—</span>}
                        </td>

                        {/* 7. Qty / Qty Kg */}
                        <td className="py-2.5 px-3 text-right font-mono text-slate-700 text-[11px]">
                          {qtyStr}
                        </td>

                        {/* 8. Total */}
                        <td
                          className={`py-2.5 px-3 text-right font-mono font-bold ${
                            isRevenue ? 'text-emerald-800' : 'text-slate-900'
                          }`}
                        >
                          {formatCurrency(e.total)}
                        </td>

                        {/* 9. Action Expand Chevron */}
                        <td className="py-2.5 px-2 text-center text-slate-400 print:hidden">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-slate-700" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </td>
                      </tr>

                      {/* Progressive Disclosure Detail Panel for Expanded Row */}
                      {isExpanded && (
                        <tr className="bg-slate-50/95 border-b border-slate-200">
                          <td colSpan={9} className="p-4">
                            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-inner space-y-3 text-xs">
                              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-slate-900">
                                    Full Transaction Details • Account [{e.ledgerNr}] {e.ledgerDescription}
                                  </span>
                                  {e.rowIndex !== null && (
                                    <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-mono">
                                      Row #{e.rowIndex}
                                    </span>
                                  )}
                                </div>
                                <span className="font-mono font-bold text-slate-900 text-sm">
                                  {formatCurrency(e.total)}
                                </span>
                              </div>

                              {/* 13-Field Grid Breakdown */}
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-[11px]">
                                <div>
                                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Posting Date</span>
                                  <span className="font-mono text-slate-800">{e.date ? formatDate(e.date) : '—'}</span>
                                </div>

                                <div>
                                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Production Batch</span>
                                  <span className="font-mono font-semibold text-slate-800">{e.batch || '—'}</span>
                                </div>

                                <div>
                                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Raiser / Contract Grower</span>
                                  <span className="font-semibold text-slate-800">{e.raiser || '—'}</span>
                                </div>

                                <div>
                                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Invoice / Reference #</span>
                                  <span className="font-mono text-slate-800">{e.invoiceRef || '—'}</span>
                                </div>

                                <div>
                                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Supplier / Vendor</span>
                                  <span className="text-slate-800">{e.supplier || '—'}</span>
                                </div>

                                <div>
                                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Customer / Buyer</span>
                                  <span className="text-slate-800">{e.customer || '—'}</span>
                                </div>

                                <div>
                                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Quantity (Head / Bags)</span>
                                  <span className="font-mono text-slate-800">{e.qty !== null ? e.qty : '—'}</span>
                                </div>

                                <div>
                                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Total Weight (Qty Kg)</span>
                                  <span className="font-mono text-slate-800">{e.qtyKg !== null ? `${formatNumber(e.qtyKg)} kg` : '—'}</span>
                                </div>

                                <div>
                                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Unit Price (₱)</span>
                                  <span className="font-mono text-slate-800">{e.price !== null ? formatCurrency(e.price) : '—'}</span>
                                </div>

                                <div className="sm:col-span-3">
                                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Description / Notes</span>
                                  <span className="text-slate-800">{e.description || '—'}</span>
                                </div>
                              </div>

                              {/* Operational Drill-down Link */}
                              {e.cycleId && (
                                <div className="pt-2 border-t border-slate-100 flex items-center justify-end">
                                  <Link
                                    to={`/cycles/${e.cycleId}`}
                                    className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-md text-[11px] font-bold transition-colors"
                                  >
                                    <span>Open Cycle Workspace</span>
                                    <ExternalLink className="w-3 h-3 ml-0.5" />
                                  </Link>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 5. Mobile Responsive Card List (< 768px, Zero Horizontal Scroll) */}
          <div className="md:hidden space-y-3">
            {filteredEntries.map((e) => {
              const isExpanded = expandedRowIds.has(e.id);
              const isRevenue = e.ledgerNr === '1010' || e.ledgerNr === '1011';

              let qtyStr = '—';
              if (e.qty && e.qtyKg) {
                qtyStr = `${e.qty} hd • ${formatNumber(e.qtyKg)} kg`;
              } else if (e.qty) {
                qtyStr = e.ledgerNr === '3020' ? `${e.qty} bags` : `${e.qty} units`;
              } else if (e.qtyKg) {
                qtyStr = `${formatNumber(e.qtyKg)} kg`;
              }

              return (
                <div
                  key={e.id}
                  onClick={() => toggleRowExpansion(e.id)}
                  className={`bg-white border rounded-xl p-3.5 shadow-sm space-y-2 cursor-pointer transition-all ${
                    isExpanded ? 'border-slate-400 bg-slate-50/50' : 'border-slate-200'
                  }`}
                >
                  {/* Mobile Card Header */}
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-slate-700">
                      {e.date ? formatDate(e.date) : '—'}
                    </span>
                    <span
                      className={`font-mono text-sm font-extrabold ${
                        isRevenue ? 'text-emerald-800' : 'text-slate-900'
                      }`}
                    >
                      {formatCurrency(e.total)}
                    </span>
                  </div>

                  {/* Mobile Card Identity */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-900">{e.raiser}</span>
                    <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-mono">
                      Batch {e.batch}
                    </span>
                  </div>

                  {/* Account Badge & Description */}
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <span
                      className={`px-1.5 py-0.5 rounded font-mono font-bold text-[10px] border ${
                        isRevenue
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                          : 'bg-slate-100 text-slate-800 border-slate-300'
                      }`}
                    >
                      {e.ledgerNr}
                    </span>
                    <span className="truncate text-slate-700 font-medium">{e.ledgerDescription}</span>
                  </div>

                  {e.description && (
                    <p className="text-[11px] text-slate-500 line-clamp-2">{e.description}</p>
                  )}

                  {/* Mobile Tap to Expand Hint */}
                  <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400">
                    <span>Qty: {qtyStr}</span>
                    <span className="flex items-center gap-0.5 text-slate-600 font-medium">
                      {isExpanded ? 'Hide Details' : 'View Full Details'}
                      {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    </span>
                  </div>

                  {/* Mobile Expanded Drawer */}
                  {isExpanded && (
                    <div className="pt-3 border-t border-slate-200 mt-2 space-y-2 text-[11px] text-slate-700 bg-white p-3 rounded-lg border border-slate-100">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-[9px] font-bold text-slate-400 uppercase block">Supplier</span>
                          <span>{e.supplier || '—'}</span>
                        </div>
                        <div>
                          <span className="text-[9px] font-bold text-slate-400 uppercase block">Customer</span>
                          <span>{e.customer || '—'}</span>
                        </div>
                        <div>
                          <span className="text-[9px] font-bold text-slate-400 uppercase block">Invoice Ref</span>
                          <span className="font-mono">{e.invoiceRef || '—'}</span>
                        </div>
                        <div>
                          <span className="text-[9px] font-bold text-slate-400 uppercase block">Unit Price</span>
                          <span className="font-mono">{e.price !== null ? formatCurrency(e.price) : '—'}</span>
                        </div>
                      </div>

                      {e.cycleId && (
                        <div className="pt-2 border-t border-slate-100 flex justify-end">
                          <Link
                            to={`/cycles/${e.cycleId}`}
                            onClick={(ev) => ev.stopPropagation()}
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded text-[10px] font-bold"
                          >
                            <span>Open Cycle Workspace</span>
                            <ExternalLink className="w-3 h-3 ml-0.5" />
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
